import { requestVerification, type Challenge } from "../shield/client";
/** Thin client for /api/v1. Cookies carry the session; unsafe calls send the CSRF token. */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

const passes = new Map<string, { pass: string; expires: number }>();
let csrfToken = "";
export const setCsrfToken = (token: string) => {
  if (csrfToken !== token) passes.clear();
  csrfToken = token;
};

export async function api<T = unknown>(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  shieldPass?: string,
  verifiedAttempt = false,
  signal?: AbortSignal,
): Promise<T> {
  const key = method + ":" + path.split("?")[0];
  const cached = passes.get(key);
  if (!shieldPass && cached && cached.expires > Date.now())
    shieldPass = cached.pass;
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      credentials: "same-origin",
      signal,
      headers: {
        accept: "application/json",
        ...(shieldPass ? { "x-forma-shield-pass": shieldPass } : {}),
        ...(body !== undefined ? { "content-type": "application/json" } : {}),
        ...(method !== "GET" ? { "x-csrf-token": csrfToken } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    if (signal?.aborted) throw error;
    throw new ApiError("NETWORK", "Network error", 0);
  }
  const data = (await res.json().catch(() => null)) as {
    error?: { code: string; message: string; requestId?: string };
    challenge?: Challenge;
  } | null;
  if (
    !res.ok &&
    data?.error?.code === "SHIELD_CHALLENGE" &&
    data.challenge &&
    !verifiedAttempt
  ) {
    try {
      const pass = await requestVerification(data.challenge);
      passes.set(key, { pass, expires: Date.now() + 240000 });
      return await api<T>(method, path, body, pass, true, signal);
    } catch (e) {
      if (e instanceof ApiError) throw e;
      throw new ApiError(
        "SHIELD_CHALLENGE",
        "Verification required",
        403,
        data.error.requestId,
      );
    }
  }
  if (!res.ok)
    throw new ApiError(
      data?.error?.code ?? "UNKNOWN",
      data?.error?.message ?? res.statusText,
      res.status,
      data?.error?.requestId,
    );
  return data as T;
}

export const errorCode = (e: unknown) =>
  e instanceof ApiError ? e.code : "UNKNOWN";
